import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
} from "@mui/material";
import { useAppSelector, useAppDispatch } from "../hooks/hooks";
import {
  setSelectedDenomination,
  setCustomAmount,
  setCurrentNavigationView,
  setSelectedWalletTab,
  setSelectedWelcomeTab,
} from "../redux/uiSlice";
import { RootState } from "../redux/store";
import PostList from "./Posts/PostList";
import PaymentRequestsList from "./payments/PaymentRequestsList";

// Get width from environment variable with fallback
const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500",
);

const WelcomeComponent: React.FC = () => {
  const theme = useTheme();
  const isMobileView = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);
  const { chainType } = useAppSelector((state) => state.wallet);
  const { selectedDenomination, userSelectedAmount } = useAppSelector(
    (state) => state.ui,
  );
  const dispatch = useAppDispatch();
  const { selectedWelcomeTab } = useAppSelector((state: RootState) => state.ui);
  const [activeTab, setActiveTab] = useState<WelcomeTab>(
    selectedWelcomeTab || "wallets",
  );

  const scrollableBoxStyles = {
    height: isMobileView ? "calc(100dvh - 370px)" : "70vh",
    overflowY: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    WebkitOverflowScrolling: "touch",
  };

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: WelcomeTab,
  ) => {
    setActiveTab(newValue);
    dispatch(setSelectedWelcomeTab(newValue));
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
      dispatch(setCustomAmount(value));
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: MOBILE_APP_WIDTH,
        margin: "auto",
        p: isMobileView ? 2 : 3,
        borderRadius: isMobileView ? 0 : 2,
        boxShadow: isMobileView ? "none" : 3,
        height: isMobileView ? "calc(100dvh - 70px)" : "calc(100vh - 180px)", // Adjust 70px based on your nav height
        minHeight: isMobileView ? "calc(100dvh - 70px)" : "calc(100vh - 180px)",
      }}
    >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ mb: 2, flexShrink: 0 }}
      >
        <Tab label="Quick Actions" value="wallets" />
        <Tab label="News" value="posts" />
        <Tab label="Payments" value="payments" />
      </Tabs>

      {activeTab === "wallets" && (
        <Box sx={scrollableBoxStyles}>
          {/* Balance Section */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              align="center"
              sx={{ fontSize: isMobileView ? "0.9rem" : "1rem" }}
            >
              {chainType === "solana"
                ? "Believe Balance Account"
                : "NdeipiCoin Balance Account"}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "baseline",
                my: 1,
              }}
            >
              <TextField
                variant="standard"
                value={userSelectedAmount}
                onChange={handleAmountChange}
                InputLabelProps={{
                  style: {
                    textAlign: "right",
                  },
                }}
                slotProps={{
                  input: {
                    style: {
                      textAlign: "right",
                      fontSize: isMobileView ? "1.75rem" : "2.125rem",
                      fontWeight: 400,
                      paddingRight: "8px",
                    },
                  },
                }}
                sx={{
                  width: isMobileView ? "130px" : "160px",
                  "& .MuiInputBase-root": {
                    "&:before, &:after": {
                      borderBottom: "none",
                    },
                  },
                }}
              />
              <Typography
                variant="h4"
                component="span"
                sx={{
                  ml: 1,
                  fontSize: isMobileView ? "1.75rem" : "2.125rem",
                }}
              >
                {selectedDenomination?.toUpperCase() || chainType !== "evm"
                  ? "NDEIPI"
                  : "NdeipiCoin"}
              </Typography>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: isMobileView ? 1 : 2,
              mb: 4,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size={isMobileView ? "small" : "medium"}
              onClick={() => {
                dispatch(setSelectedWalletTab("receive"));
                dispatch(setCurrentNavigationView("wallet"));
              }}
            >
              Receive
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size={isMobileView ? "small" : "medium"}
              onClick={() => {
                //await dispatch(fetchPolBalance());
                dispatch(setSelectedWalletTab("send"));
                dispatch(setCurrentNavigationView("wallet"));
              }}
            >
              Send
            </Button>
            {chainType === "solana" && (
              <Button
                variant="outlined"
                color="primary"
                size={isMobileView ? "small" : "medium"}
              >
                Donate
              </Button>
            )}
          </Box>

          {/* Denominations Grid */}
          {chainType !== "solana" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: isMobileView ? 1 : 2,
              }}
            >
              <Stack
                direction="row"
                spacing={isMobileView ? 1 : 2}
                justifyContent="center"
                flexWrap="wrap"
              >
                <DenominationCard
                  name="Zviri"
                  value="1/1,000,000"
                  description="of a NdeipiCoin"
                  subtext="Core accounting unit"
                  isMobile={isMobileView}
                />
                <DenominationCard
                  name="Vibes"
                  value="1/10,000"
                  description="of a NdeipiCoin"
                  subtext="In-app rewards"
                  isMobile={isMobileView}
                />
              </Stack>
              <Stack
                direction="row"
                spacing={isMobileView ? 1 : 2}
                justifyContent="center"
                flexWrap="wrap"
              >
                <DenominationCard
                  name="Nzou"
                  value="1/1,000"
                  description="of a NdeipiCoin"
                  subtext="Point-of-sale"
                  isMobile={isMobileView}
                />
                <DenominationCard
                  name="Shanga"
                  value="1/100"
                  description="of a NdeipiCoin"
                  subtext="Micro-savings"
                  isMobile={isMobileView}
                />
              </Stack>
            </Box>
          )}

          {/* Tokens Section */}
        </Box>
      )}
      {activeTab === "posts" && (
        <Box sx={scrollableBoxStyles}>
          <PostList />
        </Box>
      )}
      {activeTab === "payments" && (
        <Box sx={scrollableBoxStyles}>
          <PaymentRequestsList />
        </Box>
      )}
    </Box>
  );
};

interface DenominationCardProps {
  name: string;
  value: string;
  description: string;
  subtext: string;
  isMobile: boolean;
}

const DenominationCard: React.FC<DenominationCardProps> = ({
  name,
  value,
  description,
  subtext,
  isMobile,
}) => {
  const dispatch = useAppDispatch();
  const { selectedDenomination } = useAppSelector((state) => state.ui);
  const theme = useTheme();

  return (
    <Paper
      sx={{
        p: isMobile ? 1 : 2,
        textAlign: "center",
        height: "100%",
        backgroundColor:
          selectedDenomination === name ? "primary.main" : "background.paper",
        color: selectedDenomination === name ? "white" : "text.primary",
        width: isMobile ? "45%" : "48%",
        minWidth: isMobile ? "120px" : "48%",
        m: isMobile ? 0.5 : 0,
        cursor: "pointer",
        "&:hover": {
          "& .denomination-card-icon": {
            color: theme.palette.primary.main,
          },
          "& .denomination-card-text": {
            color: theme.palette.primary.main,
          },
        },
      }}
      elevation={1}
      onClick={() => dispatch(setSelectedDenomination(name))}
    >
      <Typography
        className="denomination-card-text"
        variant={isMobile ? "subtitle1" : "h6"}
        component="h3"
      >
        {name}
      </Typography>
      <Typography
        variant="body1"
        className="denomination-card-text"
        sx={{ fontWeight: "bold", fontSize: isMobile ? "0.9rem" : "1rem" }}
      >
        {value}
      </Typography>
      <Typography
        variant="body2"
        className="denomination-card-text"
        color="text.secondary"
        sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}
      >
        {description}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        className="denomination-card-text"
        sx={{ fontSize: isMobile ? "0.7rem" : "0.75rem" }}
      >
        {subtext}
      </Typography>
    </Paper>
  );
};

export default WelcomeComponent;
